import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { Sheet } from '../../../components/technician/ui/Sheet';
import { KEY_STATE_SPECS } from '../catalog/keyState';
import type { KeyState } from '../catalog/keyState';
import { findTarget, defaultValueForTarget } from '../targets';
import type { MappingTarget } from '../targets';
import { keyDisplayName, loadsFor } from '../model';
import type { Keypad, KeypadKey, LoadState } from '../model';
import { useKeypadStore } from '../store';
import { TargetValueControl } from './TargetValueControl';

export interface StateEditorSheetProps {
  readonly isOpen: boolean;
  readonly keypad: Keypad;
  /** Named `keypadKey`: `key` is React's own prop, and this is not it. */
  readonly keypadKey: KeypadKey;
  readonly state: KeyState;
  readonly targets: readonly MappingTarget[];
  readonly onClose: () => void;
}

/** What the output picker has to offer, said as what it is. */
const addableHint = (configured: number, spare: number): string => {
  if (configured === 0) {
    return 'This RCU has no outputs configured yet. Add the relays and rails under RCU outputs and DIN rails first.';
  }
  if (spare === 0) return 'Every output in the configuration is already in this state.';
  return `${spare} of the ${configured} configured outputs are not in this state yet.`;
};

/**
 * Everything one key state drives.
 *
 * The list is the state: adding an output puts it on the list with the value
 * that output starts at, and every later change is the whole list again. Nothing
 * is applied half way — a change that the feature refuses leaves the list as it
 * was and says why, so what is on screen is always what would be written.
 */
export const StateEditorSheet: React.FC<StateEditorSheetProps> = ({
  isOpen,
  keypad,
  keypadKey,
  state,
  targets,
  onClose,
}) => {
  const loads = loadsFor(keypadKey, state);
  const notice = useKeypadStore((store) => store.notice);
  const setStateLoads = useKeypadStore((store) => store.setStateLoads);
  const clearNotice = useKeypadStore((store) => store.clearNotice);
  const [candidate, setCandidate] = useState('');

  const spare = targets.filter(
    (target) => !loads.some((load) => load.targetId === target.id),
  );
  const spec = KEY_STATE_SPECS[state];

  const commit = (next: readonly LoadState[]): void => {
    setStateLoads(targets, keypad.id, keypadKey.id, state, next);
  };

  const setValue = (targetId: string, value: number): void => {
    commit(
      loads.map((load) => (load.targetId === targetId ? { targetId, value } : load)),
    );
  };

  const add = (target: MappingTarget): void => {
    commit([...loads, { targetId: target.id, value: defaultValueForTarget(target) }]);
    setCandidate('');
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={keyDisplayName(keypad, keypadKey)}
      subtitle={`${spec.label} · ${spec.hint}`}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-4">
        {notice !== null && (
          <div className="flex items-start justify-between gap-3 rounded-xl bg-[#FDECEA] px-4 py-3 text-sm text-[#C0392B]">
            <span>{notice.text}</span>
            <button type="button" onClick={clearNotice} className="font-medium hover:underline">
              Dismiss
            </button>
          </div>
        )}

        {loads.length === 0 && (
          <p className="text-sm text-[#666666]">
            This key state drives nothing yet. Add an output below.
          </p>
        )}

        {loads.map((load) => {
          const target = findTarget(targets, load.targetId);
          return (
            <div key={load.targetId} className="rounded-xl border border-[#E0E0E0] p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#333333]">
                    {target?.label ?? 'Output no longer in the configuration'}
                  </p>
                  <p className="text-xs text-[#666666]">{target?.detail ?? load.targetId}</p>
                </div>
                <button
                  type="button"
                  aria-label="Remove this output"
                  onClick={() => commit(loads.filter((entry) => entry.targetId !== load.targetId))}
                  className="rounded-full p-2 text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#C0392B]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {target === undefined ? (
                <p className="text-sm text-[#C0392B]">
                  This state points at an output the configuration does not offer, so it cannot be
                  written. Remove it, or add the output back under Loads.
                </p>
              ) : (
                <TargetValueControl
                  target={target}
                  value={load.value}
                  onChange={(value) => setValue(load.targetId, value)}
                />
              )}
            </div>
          );
        })}

        <div className="rounded-xl border border-dashed border-[#E0E0E0] p-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-[#333333]">Add an output</span>
            <select
              value={candidate}
              onChange={(event) => setCandidate(event.target.value)}
              className="h-12 w-full rounded-xl border border-[#E0E0E0] bg-white px-4 text-[#333333] outline-none focus:border-[#D4A054] focus:ring-2 focus:ring-[#D4A054]/30"
            >
              <option value="">Choose an output…</option>
              {spare.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label} — {target.detail}
                </option>
              ))}
            </select>
          </label>
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-[#666666]">{addableHint(targets.length, spare.length)}</p>
            <Button
              variant="secondary"
              disabled={candidate === ''}
              onClick={() => {
                const target = findTarget(targets, candidate);
                if (target !== undefined) add(target);
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </Sheet>
  );
};

export default StateEditorSheet;
